import { SearchType } from '@brightdrop/bd-ui';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ChargeStationDto } from '~/api/generated-api';
import type {
  Availability,
  ChargeProvider,
  ChargingGrade,
  ConnectorTypeFilter,
} from '~/common/models/chargeStation.model';

export enum ChargeStationsFilterType {
  CONNECTOR_TYPE = 'connectorType',
  CHARGING_GRADE = 'grade',
  PROVIDER = 'provider',
  AVAILABILITY = 'availability',
}

export interface ChargeStationsFilterCriteria {
  [ChargeStationsFilterType.CONNECTOR_TYPE]: ConnectorTypeFilter[];
  [ChargeStationsFilterType.CHARGING_GRADE]: ChargingGrade[];
  [ChargeStationsFilterType.PROVIDER]: ChargeProvider[];
  [ChargeStationsFilterType.AVAILABILITY]: Availability[];
}

export interface ChargeStationsSearchCriteria {
  searchType: SearchType;
  input: string;
}

export interface ChargeStationsState {
  searchCriteria: ChargeStationsSearchCriteria;
  filterCriteria: ChargeStationsFilterCriteria;
  currentLocation?: {
    latitude: number;
    longitude: number;
  };
  /** Search radius in km (min distance from center to map bounds). Sent as searchRadiusInKilometers. */
  searchRadiusInKilometers?: number;
  /** Currently hovered station (from list or map) */
  hoveredStation: ChargeStationDto | null;
  /** Currently selected station (showing details) */
  selectedStation: ChargeStationDto | null;
  /** True while the map is animating a selection-driven flyTo (suppresses re-fetch) */
  selectionPanInProgress: boolean;
  /** Charge stations from the search result (shared between list and map) */
  accumulatedStations: ChargeStationDto[];
}

const initialState: ChargeStationsState = {
  searchCriteria: {
    searchType: SearchType.CHARGE_STATION,
    input: '',
  },
  filterCriteria: {
    [ChargeStationsFilterType.CONNECTOR_TYPE]: [],
    [ChargeStationsFilterType.CHARGING_GRADE]: [],
    [ChargeStationsFilterType.PROVIDER]: [],
    [ChargeStationsFilterType.AVAILABILITY]: [],
  },
  hoveredStation: null,
  selectedStation: null,
  selectionPanInProgress: false,
  accumulatedStations: [],
};

const chargeStationsSlice = createSlice({
  name: 'chargeStations',
  initialState,
  reducers: {
    updateSearchCriteria: (
      state,
      action: PayloadAction<Partial<ChargeStationsSearchCriteria>>
    ) => {
      state.searchCriteria = { ...state.searchCriteria, ...action.payload };
    },
    updateFilterCriteria: (
      state,
      action: PayloadAction<Partial<ChargeStationsFilterCriteria>>
    ) => {
      state.filterCriteria = { ...state.filterCriteria, ...action.payload };
    },
    setCurrentLocation: (
      state,
      action: PayloadAction<{ latitude: number; longitude: number }>
    ) => {
      state.currentLocation = action.payload;
    },
    setSearchRadiusInKilometers: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.searchRadiusInKilometers = action.payload;
    },
    clearFilters: (state) => {
      state.filterCriteria = initialState.filterCriteria;
    },
    clearSearch: (state) => {
      state.searchCriteria = initialState.searchCriteria;
    },
    clearAll: (state) => {
      state.searchCriteria = initialState.searchCriteria;
      state.filterCriteria = initialState.filterCriteria;
      state.currentLocation = undefined;
      state.searchRadiusInKilometers = undefined;
      state.hoveredStation = null;
      state.selectedStation = null;
      state.selectionPanInProgress = false;
      state.accumulatedStations = [];
    },
    setHoveredStation: (
      state,
      action: PayloadAction<ChargeStationDto | null>
    ) => {
      state.hoveredStation = action.payload;
    },
    clearHoveredStation: (state) => {
      state.hoveredStation = null;
    },
    setSelectedStation: (
      state,
      action: PayloadAction<ChargeStationDto | null>
    ) => {
      const stationChanged = action.payload?.id !== state.selectedStation?.id;
      state.selectedStation = action.payload;
      state.hoveredStation = null;
      if (stationChanged) {
        state.selectionPanInProgress = action.payload !== null;
      }
    },
    clearSelectedStation: (state) => {
      state.selectedStation = null;
      state.hoveredStation = null;
      state.selectionPanInProgress = false;
    },
    clearSelectionPanInProgress: (state) => {
      state.selectionPanInProgress = false;
    },
    setAccumulatedStations: (
      state,
      action: PayloadAction<ChargeStationDto[]>
    ) => {
      state.accumulatedStations = action.payload;
    },
  },
});

export const {
  updateSearchCriteria,
  updateFilterCriteria,
  setCurrentLocation,
  setSearchRadiusInKilometers,
  clearFilters,
  clearSearch,
  clearAll,
  setHoveredStation,
  clearHoveredStation,
  setSelectedStation,
  clearSelectedStation,
  clearSelectionPanInProgress,
  setAccumulatedStations,
} = chargeStationsSlice.actions;

export default chargeStationsSlice.reducer;
